/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 */
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import usePlans from "../plans/usePlans";
import { formatNumber } from "utils/formatNumber";
import Reveal from "./Reveal";

export default function PricingSection({ isArabic }) {
  const navigate = useNavigate();
  const { plans, loading } = usePlans(isArabic);

  const featuredId =
    plans.length > 1
      ? plans[Math.min(1, plans.length - 1)]?.id
      : plans[0]?.id;

  return (
    <section className="gl-section" id="plans-section">
      <ToastContainer position="top-center" autoClose={3000} rtl={isArabic} />
      <div className="gl-shell">
        <Reveal className="gl-head">
          <p className="gl-eyebrow">
            {isArabic ? "الباقات" : "Pricing"}
          </p>
          <h2 className="gl-title">
            {isArabic ? "اختر مدار نموّك" : "Choose your growth orbit"}
          </h2>
          <p className="gl-lead">
            {isArabic
              ? "باقات شفافة تناسب حجم متجرك — ابدأ صغيراً وتوسّع بلا حدود."
              : "Transparent plans for every store size — start lean and scale freely."}
          </p>
        </Reveal>

        {loading ? (
          <div className="gl-loading">
            <div className="gl-spinner" />
            <p>{isArabic ? "جارِ تحميل الباقات..." : "Loading plans..."}</p>
          </div>
        ) : plans.length === 0 ? (
          <p className="gl-loading">
            {isArabic
              ? "لا توجد باقات متاحة حالياً"
              : "No plans available at the moment"}
          </p>
        ) : (
          <div className="gl-plans">
            {plans.map((plan, i) => {
              const featured = plan.id === featuredId;
              return (
                <Reveal key={plan.id} delay={i * 0.08}>
                  <article
                    className={`gl-plan${featured ? " gl-plan--featured" : ""}`}
                  >
                    {featured ? (
                      <span className="gl-plan__badge">
                        {isArabic ? "الأكثر شعبية" : "Most Popular"}
                      </span>
                    ) : null}

                    <h3 className="gl-plan__title">{plan.title}</h3>
                    {plan.desc ? (
                      <p className="gl-plan__desc">{plan.desc}</p>
                    ) : (
                      <p className="gl-plan__desc">&nbsp;</p>
                    )}

                    <div className="gl-plan__price">
                      <strong>
                        {formatNumber(Number(plan.price) || 0)}{" "}
                        {isArabic ? "د.ع" : "IQD"}
                      </strong>
                      <small>
                        {plan.price === 0
                          ? isArabic
                            ? "/ أسبوع"
                            : "/ week"
                          : isArabic
                            ? "/ شهر"
                            : "/ month"}
                      </small>
                    </div>

                    {Array.isArray(plan.notes) && plan.notes.length > 0 ? (
                      <ul className="gl-plan__list">
                        {plan.notes.map((note, idx) => (
                          <li key={idx}>
                            <Check size={16} strokeWidth={2.5} aria-hidden />
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <button
                      type="button"
                      className="gl-btn gl-btn--gold"
                      onClick={() => navigate("/signup")}
                    >
                      {isArabic ? "اشترك الآن" : "Subscribe Now"}
                    </button>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
